import React, { useState } from "react";
import {
  Link,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  Navigate,
} from "react-router-dom";
import { GuestResponse } from "./AdminGuests";
import { BASE_API_URL } from "store/Me";
import axios from "axios";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import GuestInvites from "./GuestInvites";
import GuestCredentials from "./GuestCredentials";
import GuestKeyRequests from "./GuestKeyRequests";
import GuestKeyRings from "./GuestKeyRings";
import GuestLockActivity from "./GuestLockActivity";
import GuestOnboardingModal from "components/shared/GuestOnboardingModal";
import GuestPropertyAccess, {
  useGetGuestPropertyAccess,
} from "./GuestPropertyAccess";
import GuestTemporaryAccess from "./GuestTemporaryAccess";
import { formatDateOrDefault } from "helpers";
import { ArrowLeft } from "lucide-react";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "components/ui/tabs";

const navLinks = [
  { to: "invites", label: "Invites" },
  { to: "credentials", label: "Credentials" },
  { to: "key-requests", label: "Key Requests" },
  { to: "lock-activity", label: "Lock Activity" },
  { to: "property-access", label: "Property Access" },
  { to: "temporary-access", label: "Temporary Access" },
];

export default function AdminGuest() {
  const { guestId } = useParams<{ guestId: string }>();
  const { data } = useGetGuest(guestId!);
  const { data: propertyAccesses } = useGetGuestPropertyAccess(guestId!);
  const queryClient = useQueryClient();
  const [onboardModalOpen, setOnboardModalOpen] = useState(false);

  if (!data) {
    return (
      <main className="py-6 mx-auto space-y-6 max-w-screen-2xl">
        <Link
          to="/admin/guests"
          className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-blue-700"
        >
          <ArrowLeft className="size-4" />
          Back to Guests
        </Link>
        <div className="flex items-center justify-center text-sm bg-white border rounded-lg shadow-sm min-h-80 border-slate-200 text-slate-500">
          Loading guest...
        </div>
      </main>
    );
  }

  return (
    <main className="py-6 mx-auto space-y-6 max-w-screen-2xl">
      <header className="pb-5 border-b border-slate-200 sm:flex sm:items-center sm:justify-between">
        <div>
          <Link
            to="/admin/guests"
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-blue-700"
          >
            <ArrowLeft className="size-4" />
            Back to Guests
          </Link>
          <h1 className="mt-2 text-2xl font-semibold text-slate-900">
            {data.firstName} {data.lastName}
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            {data.guestTypes.length > 0 ? data.guestTypes.join(", ") : "Guest"}
          </p>
        </div>
        <Button className="mt-4 sm:mt-0" onClick={() => setOnboardModalOpen(true)} type="button">
          Update Onboarding
        </Button>
      </header>
      <div className="grid gap-6 xl:grid-cols-[minmax(19rem,24rem)_minmax(0,1fr)]">
        <aside className="space-y-4 xl:sticky xl:top-6 xl:self-start">
          <Details guest={data} />
        </aside>
        <section className="min-w-0 space-y-6">
          <GuestKeyRings guestId={data.id} guestTypes={data.guestTypes} />
          <Navigation guestId={guestId!} />
          <div>
            <Routes>
              <Route path="invites" element={<GuestInvites guestId={data.id} />} />
              <Route
                path="credentials"
                element={<GuestCredentials guestId={data.id} />}
              />
              <Route
                path="key-requests"
                element={<GuestKeyRequests guestId={data.id} />}
              />
              <Route
                path="lock-activity"
                element={<GuestLockActivity guestId={data.id} />}
              />
              <Route
                path="property-access"
                element={<GuestPropertyAccess guestId={data.id} />}
              />
              <Route
                path="temporary-access"
                element={<GuestTemporaryAccess guestId={data.id} />}
              />
              <Route path="*" element={<Navigate to="invites" replace />} />
            </Routes>
          </div>
        </section>
      </div>
      <GuestOnboardingModal
        open={onboardModalOpen}
        guestRecord={data}
        propertyAccesses={propertyAccesses}
        onClose={(submitted) => {
          setOnboardModalOpen(false);
          if (submitted) {
            queryClient.invalidateQueries({
              queryKey: ["guests", "details", data.id],
            });
            queryClient.invalidateQueries({
              queryKey: ["guests", "property-access", data.id],
            });
          }
        }}
      />
    </main>
  );
}

function Details({ guest }: { guest: GuestResponse }) {
  const isDeleted = !!guest.deletedTimestamp;
  return (
    <section className="overflow-hidden bg-white border rounded-lg shadow-sm border-slate-200">
      <header className="flex items-center justify-between gap-4 px-4 py-3 border-b border-slate-200">
        <div>
          <h2 className="text-base font-semibold text-slate-900">Guest details</h2>
          <p className="mt-0.5 text-xs text-slate-500">Contact and validity</p>
        </div>
        <Badge
          className={isDeleted ? undefined : "border-green-500 bg-green-50 text-green-600"}
          variant={isDeleted ? "destructive" : "outline"}
        >
          {isDeleted ? "Deleted" : "Active"}
        </Badge>
      </header>
      <dl className="divide-y divide-slate-100">
        <DetailItem label="Email">
          <a
            href={`mailto:${guest.email}`}
            className="text-blue-600 break-all hover:underline"
          >
            {guest.email}
          </a>
        </DetailItem>
        <DetailItem label="Guest Types">
          {guest.guestTypes.length > 0
            ? guest.guestTypes.join(", ")
            : "No Guest Types"}
        </DetailItem>
        <DetailItem label="Valid From">
          {formatDateOrDefault(guest.validFrom)}
        </DetailItem>
        <DetailItem label="Valid To">
          {formatDateOrDefault(guest.validTo)}
        </DetailItem>
      </dl>
    </section>
  );
}

function DetailItem({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] gap-4 px-4 py-3">
      <dt className="text-xs font-semibold tracking-wide uppercase text-slate-500">
        {label}
      </dt>
      <dd className="min-w-0 text-sm font-medium wrap-break-word text-slate-800">
        {children}
      </dd>
    </div>
  );
}

function Navigation({ guestId }: { guestId: string }) {
  const location = useLocation();
  const navigate = useNavigate();

  const lastSegment = location.pathname.split("/").filter(Boolean).pop();
  const activeTo =
    navLinks.find(({ to }) => to === lastSegment)?.to ?? navLinks[0].to;

  return (
    <Tabs
      value={activeTo}
      onValueChange={(value) => navigate(`/admin/guests/${guestId}/${value}`)}
    >
      <TabsList className="justify-start w-full overflow-x-auto overflow-y-hidden">
        {navLinks.map(({ to, label }) => (
          <TabsTrigger
            key={to}
            value={to}
            className="shrink-0 data-[state=active]:bg-slate-200!"
          >
            {label}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}

async function getGuest(guestId: string) {
  const url = `${BASE_API_URL}/api/guest/${guestId}`;
  const response = await axios.get<GuestResponse>(url);
  return response.data;
}

function useGetGuest(guestId: string) {
  return useQuery({
    queryKey: ["guests", "details", guestId],
    queryFn: () => getGuest(guestId),
  });
}
